// REST API クライアント。
// 認証モードに応じた ApiRequest を注入し、required 環境では Bearer トークン付きで呼び出す。
// JSON 不一致など本来発生しないケースは throw して TanStack Query 側で扱う。

import { useMemo } from "react";
import type { CanvasObject, CanvasSizePreset } from "@refinement-canvas/domain";
import { useApiRequest, type ApiRequest } from "../auth/api-request-context";

interface BootstrapResponse {
  canvasId: string;
  serverSeq: number;
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
    thumbnailUrl?: string | undefined;
    /** キャンバスサイズのプリセット（小 / 中 / 大）。未設定はクライアントで既定=中(10K)として扱う。 */
    sizePreset?: CanvasSizePreset | undefined;
    /** @internal 参考情報のみ（META の素通し）。実体ありかつ非空かは export-url が最終判定する。UI 指標に使わない。 */
    exportKey?: string | undefined;
    isFavorite?: boolean | undefined;
    /** ゴミ箱にあり、参照専用として扱うか。旧サーバ互換のため optional。 */
    isTrashed?: boolean | undefined;
  };
  objects: readonly CanvasObject[];
}

export interface RecentCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  lastOpenedAt: string;
}

export interface FavoriteCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  updatedAt: string;
  favoritedAt: string;
}

export interface AllCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  updatedAt: string;
}

export interface TrashedCanvasItem {
  canvasId: string;
  title: string;
  thumbnailKey?: string | undefined;
  thumbnailUrl?: string | undefined;
  trashedAt: string;
}

/** GET /api/me のレスポンス（権限フラグを含むアクター情報）。 */
export interface MeResponse {
  actor: {
    userId: string;
    displayName: string;
    permissions: {
      canRead: boolean;
      canWrite: boolean;
      canAdmin: boolean;
    };
  };
}

export interface OgFetchResult {
  url: string;
  title?: string | undefined;
  description?: string | undefined;
  image?: string | undefined;
  siteName?: string | undefined;
}

export interface RealtimeSessionTicket {
  token: string;
  expiresAt: string;
}

interface PresignedUpload {
  uploadUrl: string;
  bucket: string;
  key: string;
  expiresInSeconds: number;
}

/**
 * 全体像 PDF の presign 応答。PUT 先は配信キーではなく staging 一意キー
 * （exports-staging/<canvasId>/<uploadId>.pdf）で、finalize がこの uploadId で staging 実体を照合し、検証を
 * 通ったものだけ配信キーへ昇格する。クライアントは PUT 時に x-amz-meta-export-upload-id ヘッダーへ同じ値を載せ、
 * finalize へこの uploadId を渡す。
 */
interface ExportPresignedUpload extends PresignedUpload {
  uploadId: string;
}

interface UpdateCanvasMetaResponse {
  meta: {
    title: string;
    createdAt: string;
    updatedAt: string;
    thumbnailKey?: string | undefined;
    thumbnailUrl?: string | undefined;
    sizePreset?: CanvasSizePreset | undefined;
    /** @internal 参考情報のみ（META の素通し）。実体ありかつ非空かは export-url が最終判定する。UI 指標に使わない。 */
    exportKey?: string | undefined;
  };
}

interface FinalizeExportResponse {
  // 今回のアップロードを配信キーの current として昇格し exportUpdatedAt を確定したか。
  // false は世代ゲート（描画後に他者の drawable 編集が確定）や昇格後の再確認で stale と判定され、昇格しなかった
  // または昇格後に版を取り消した等で確定できなかったことを示す。クライアントは dirty を保持して新しい uploadId で
  // 再保存し、最新の全体像へ収束させる（確定時刻の停滞を残さない）。
  confirmed: boolean;
  exportKey?: string | undefined;
  updatedAt: string;
}

export interface CanvasApiClient {
  /**
   * キャンバスのスナップショットを取得する。未作成キャンバスはサーバ側で lazy create される。
   * createName を渡すと、その lazy create 時の初期キャンバス名として使われる（既存キャンバスでは無視）。
   */
  readonly fetchCanvasBootstrap: (
    canvasId: string,
    createName?: string,
  ) => Promise<BootstrapResponse>;
  readonly fetchRecentCanvases: () => Promise<{ items: readonly RecentCanvasItem[] }>;
  readonly deleteRecentCanvas: (canvasId: string) => Promise<void>;
  /** 現在ログインしているユーザーの権限情報を取得する（管理者判定に使う）。 */
  readonly fetchMe: () => Promise<MeResponse>;
  /** ログインユーザーのお気に入りキャンバス一覧（登録日時の新しい順）。 */
  readonly fetchFavorites: () => Promise<{ items: readonly FavoriteCanvasItem[] }>;
  /** 現在のキャンバスをお気に入り登録する。 */
  readonly addFavorite: (canvasId: string) => Promise<void>;
  /** 現在のキャンバスのお気に入りを解除する。 */
  readonly removeFavorite: (canvasId: string) => Promise<void>;
  /** 管理者専用: 全キャンバス一覧（更新日時の新しい順・最大 48 件）。 */
  readonly fetchAllCanvases: () => Promise<{ items: readonly AllCanvasItem[] }>;
  /** 現在の利用者から見えるゴミ箱キャンバス一覧。 */
  readonly fetchTrash: () => Promise<{ items: readonly TrashedCanvasItem[] }>;
  /** キャンバスを可逆なゴミ箱状態へ移す。 */
  readonly trashCanvas: (canvasId: string) => Promise<void>;
  /** キャンバスをゴミ箱から戻す。 */
  readonly restoreCanvas: (canvasId: string) => Promise<void>;
  readonly updateCanvasMeta: (
    canvasId: string,
    input: {
      title?: string | undefined;
      thumbnailKey?: string | null | undefined;
    }
  ) => Promise<UpdateCanvasMetaResponse>;
  /**
   * 全体像 PDF の presigned PUT 完了後に呼び、サーバが S3 実体を HeadObject で確認してから exportKey を確定する。
   * exportKey は汎用メタ更新では立てられないため、確定はこの経路に限定する（実体の無い exportKey を防ぐ）。
   */
  readonly finalizeExportUpload: (params: {
    canvasId: string;
    uploadId: string;
    // PDF を描画した時点のサーバ確定世代。サーバはこれを現在の最新世代と比較し、描画後に drawable 変更が確定して
    // いれば（同時編集の遅延受信を含む）confirmed=false を返し、最新状態での再保存を促す。
    serverSeq: number;
  }) => Promise<FinalizeExportResponse>;
  readonly createCanvasObject: (
    canvasId: string,
    object: CanvasObject
  ) => Promise<{ object: CanvasObject }>;
  readonly patchCanvasObject: (
    canvasId: string,
    objectId: string,
    input: PatchObjectInput
  ) => Promise<{ object: CanvasObject }>;
  readonly deleteCanvasObject: (canvasId: string, objectId: string) => Promise<void>;
  readonly presignImageUpload: (params: {
    canvasId: string;
    objectId: string;
    contentType: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignVideoUpload: (params: {
    canvasId: string;
    objectId: string;
    contentType: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignThumbnailUpload: (params: {
    canvasId: string;
    contentLength: number;
  }) => Promise<PresignedUpload>;
  readonly presignExportUpload: (params: {
    canvasId: string;
    contentLength: number;
  }) => Promise<ExportPresignedUpload>;
  readonly notifyUploadComplete: (
    canvasId: string,
    bucket: string,
    key: string
  ) => Promise<UploadCompleteResult>;
  readonly fetchOgp: (url: string) => Promise<OgFetchResult>;
  /**
   * OGP リンクカードの画像を、同一オリジンの中継（GET /api/og/image）経由で取得する。
   * 相手サイトの Cross-Origin-Resource-Policy / CORS ヘッダーに依存せず読めるようにするための経路。
   */
  readonly fetchOgpImage: (imageUrl: string) => Promise<Blob>;
  readonly issueRealtimeSession: (canvasId: string) => Promise<RealtimeSessionTicket>;
}

/**
 * API がエラー応答（非 2xx）を返したことを表すエラー。
 * 呼び出し側が status を見て「恒久的な失敗（再取得しても同じ）」と「回復し得る失敗（認証失効など）」を
 * 区別できるようにするために持つ。
 */
export class CanvasApiHttpError extends Error {
  constructor(
    readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "CanvasApiHttpError";
  }
}

async function callJson<T>(request: ApiRequest, path: string, init?: RequestInit): Promise<T> {
  const response = await request(path, init);
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new Error(`API ${response.status}: ${body || response.statusText}`);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** バイナリ応答（画像など）を Blob として受け取る。JSON 前提の callJson は使えないため分ける。 */
async function callBlob(request: ApiRequest, path: string): Promise<Blob> {
  const response = await request(path);
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new CanvasApiHttpError(response.status, `API ${response.status}: ${body || response.statusText}`);
  }
  return await response.blob();
}

// ---- canvas ----

export interface PatchObjectInput {
  transform?:
    | {
        x?: number | undefined;
        y?: number | undefined;
        w?: number | undefined;
        h?: number | undefined;
        rotation?: number | undefined;
        zOrder?: number | undefined;
      }
    | undefined;
  text?: string | undefined;
}

export interface UploadCompleteResult {
  ok: boolean;
  /** CloudFront 経由の公開 URL。未設定（ATTACHMENTS_PUBLIC_BASE_URL 未指定）の場合は null。 */
  publicUrl: string | null;
}

export function createCanvasApiClient(request: ApiRequest): CanvasApiClient {
  return {
    fetchCanvasBootstrap(canvasId, createName) {
      // 新規作成時の初期名を引き継ぐため、指定があれば ?name= を付与する（サーバは lazy create 時のみ採用）。
      const query =
        createName !== undefined && createName.length > 0
          ? `?name=${encodeURIComponent(createName)}`
          : "";
      return callJson<BootstrapResponse>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}${query}`,
      );
    },
    fetchRecentCanvases() {
      return callJson<{ items: readonly RecentCanvasItem[] }>(request, "/me/recent-canvases");
    },
    deleteRecentCanvas(canvasId) {
      return callJson<void>(request, `/me/recent-canvases/${encodeURIComponent(canvasId)}`, {
        method: "DELETE",
      });
    },
    fetchMe() {
      return callJson<MeResponse>(request, "/me");
    },
    fetchFavorites() {
      return callJson<{ items: readonly FavoriteCanvasItem[] }>(request, "/me/favorites");
    },
    addFavorite(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/favorite`, {
        method: "PUT",
      });
    },
    removeFavorite(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/favorite`, {
        method: "DELETE",
      });
    },
    fetchAllCanvases() {
      return callJson<{ items: readonly AllCanvasItem[] }>(request, "/admin/canvases");
    },
    fetchTrash() {
      return callJson<{ items: readonly TrashedCanvasItem[] }>(request, "/me/trash");
    },
    trashCanvas(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/trash`, {
        method: "PUT",
      });
    },
    restoreCanvas(canvasId) {
      return callJson<void>(request, `/canvases/${encodeURIComponent(canvasId)}/trash`, {
        method: "DELETE",
      });
    },
    updateCanvasMeta(canvasId, input) {
      return callJson<UpdateCanvasMetaResponse>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}`,
        { method: "PUT", body: JSON.stringify(input) }
      );
    },
    createCanvasObject(canvasId, object) {
      return callJson<{ object: CanvasObject }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects`,
        { method: "POST", body: JSON.stringify({ object }) }
      );
    },
    patchCanvasObject(canvasId, objectId, input) {
      return callJson<{ object: CanvasObject }>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
        { method: "PATCH", body: JSON.stringify(input) }
      );
    },
    deleteCanvasObject(canvasId, objectId) {
      return callJson<void>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/objects/${encodeURIComponent(objectId)}`,
        { method: "DELETE" }
      );
    },
    presignImageUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/uploads:image`,
        {
          method: "POST",
          body: JSON.stringify({
            objectId: params.objectId,
            contentType: params.contentType,
            contentLength: params.contentLength,
          }),
        }
      );
    },
    presignVideoUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/uploads:video`,
        {
          method: "POST",
          body: JSON.stringify({
            objectId: params.objectId,
            contentType: params.contentType,
            contentLength: params.contentLength,
          }),
        }
      );
    },
    presignThumbnailUpload(params) {
      return callJson<PresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/thumbnail`,
        {
          method: "POST",
          body: JSON.stringify({ contentLength: params.contentLength }),
        }
      );
    },
    presignExportUpload(params) {
      return callJson<ExportPresignedUpload>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/export`,
        {
          method: "POST",
          body: JSON.stringify({ contentLength: params.contentLength }),
        }
      );
    },
    finalizeExportUpload(params) {
      return callJson<FinalizeExportResponse>(
        request,
        `/canvases/${encodeURIComponent(params.canvasId)}/export/finalize`,
        {
          method: "POST",
          body: JSON.stringify({ uploadId: params.uploadId, serverSeq: params.serverSeq }),
        }
      );
    },
    notifyUploadComplete(canvasId, bucket, key) {
      return callJson<UploadCompleteResult>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/uploads:complete`,
        {
          method: "POST",
          body: JSON.stringify({ bucket, key }),
        }
      );
    },
    fetchOgp(url) {
      return callJson<OgFetchResult>(request, `/og?url=${encodeURIComponent(url)}`);
    },
    fetchOgpImage(imageUrl) {
      return callBlob(request, `/og/image?url=${encodeURIComponent(imageUrl)}`);
    },
    issueRealtimeSession(canvasId) {
      return callJson<RealtimeSessionTicket>(
        request,
        `/canvases/${encodeURIComponent(canvasId)}/realtime-session`
      );
    },
  };
}

export function useCanvasApi(): CanvasApiClient {
  const request = useApiRequest();
  return useMemo(() => createCanvasApiClient(request), [request]);
}
